// Thin API client for the system-broadcast endpoints in rd (PLEX-4102).
//
// Mirrors releaseNotesApi.js: resolve the endpoint from config
// (ExternalRESTAPI.<Key>), build an absolute URL against the current origin,
// and drive the shared RequestBuilder (auth + retry + msgpack handling). Every
// call chains `.then(toJSON)` — RequestBuilder.fetch() resolves to the raw
// Response, so callers reading the JSON body must unwrap it.

import { getConfig } from "../lib/configMgr";
import { RequestBuilder, toJSON } from "./request";

// Build an absolute URL for an admin-broadcasts path. `id` (optional) is
// percent-encoded; `suffix` appends an action segment like "/activate".
const adminUrl = (id = null, suffix = "") => {
  const { ExternalRESTAPI } = getConfig();
  const base = ExternalRESTAPI.AdminBroadcasts;
  const path = id == null ? base : `${base}/${encodeURIComponent(id)}${suffix}`;
  return new URL(path, window.location.origin);
};

/**
 * PUBLIC — active broadcasts for the polling banner.
 * Resolves to {broadcasts: [{id, message, level}]} (may be empty). Callers
 * tolerate errors/empty and render nothing.
 */
export const fetchActiveBroadcasts = (authData) => {
  const { ExternalRESTAPI } = getConfig();
  const url = new URL(ExternalRESTAPI.Broadcasts, window.location.origin);
  return new RequestBuilder(url).withAuthorization(authData).fetch().then(toJSON);
};

/** ADMIN — every broadcast with full status/metadata. */
export const fetchAdminBroadcasts = (authData) =>
  new RequestBuilder(adminUrl())
    .withAuthorization(authData)
    .withJSONContentType()
    .fetch()
    .then(toJSON);

/** ADMIN — create a broadcast. `level` is optional (backend defaults it). */
export const createBroadcast = (authData, { message, level } = {}) =>
  new RequestBuilder(adminUrl())
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify({ message, level }))
    .fetch()
    .then(toJSON);

/** ADMIN — partial update (message and/or level). */
export const updateBroadcast = (authData, id, payload) =>
  new RequestBuilder(adminUrl(id))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPUT(JSON.stringify(payload))
    .fetch()
    .then(toJSON);

/** ADMIN — mark a broadcast active (shows in the banner). */
export const activateBroadcast = (authData, id) =>
  new RequestBuilder(adminUrl(id, "/activate"))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify({}))
    .fetch()
    .then(toJSON);

/** ADMIN — mark a broadcast inactive (hides it from the banner). */
export const deactivateBroadcast = (authData, id) =>
  new RequestBuilder(adminUrl(id, "/deactivate"))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify({}))
    .fetch()
    .then(toJSON);
